import { useCallback, useEffect, useRef, useState } from 'react'

function readValue<T>(key: string, initial: T): T {
  if (key === '' || typeof window === 'undefined') return initial
  try {
    const raw = window.localStorage.getItem(key)
    if (raw === null) return initial
    return JSON.parse(raw) as T
  } catch {
    return initial
  }
}

export function useLocalStorage<T>(
  key: string,
  initial: T,
): [T, (value: T) => void] {
  const [value, setValue] = useState<T>(() => readValue(key, initial))
  const lastKey = useRef(key)

  useEffect(() => {
    if (lastKey.current === key) return
    lastKey.current = key
    setValue(readValue(key, initial))
  }, [key, initial])

  const update = useCallback(
    (next: T) => {
      setValue(next)
      if (key === '' || typeof window === 'undefined') return
      try {
        if (next === null || next === undefined) {
          window.localStorage.removeItem(key)
        } else {
          window.localStorage.setItem(key, JSON.stringify(next))
        }
      } catch {
        // localStorage write failed (quota / private mode) — keep in-memory state only
      }
    },
    [key],
  )

  return [value, update]
}
